/**
 * A renewal that failed because Firebase has no session, and waiting did not
 * bring one back.
 *
 * WHY A SEPARATE ERROR AND NOT A MESSAGE TO MATCH. Its sibling,
 * theFirebaseAccountIsGone, has to read somebody else's wording because the
 * iOS plugin loses the error code on the way through the bridge. This one does
 * not have that problem: the fact that decides it is ours. We asked Firebase
 * whether anybody was signed in, waited three seconds, and asked again. A
 * `never-came` is a measurement we made, so it travels as a type rather than
 * as a sentence somebody could rephrase.
 *
 * WHAT IT MEANS, AND WHY IT ENDS A SESSION. The member is holding a session
 * the application can no longer renew. Nothing about that recovers on its own:
 * the next renewal asks the same question and gets the same answer. Their own
 * API token keeps working until it expires, and then they are stuck on a
 * screen waiting for data that is never going to arrive.
 *
 * Signing them out puts them on the login screen, where signing in rebuilds
 * the Firebase session and everything works again. It is the only exit, and it
 * is the same reasoning as for a deleted account.
 *
 * WHAT IT IS NOT. Not a race at launch: that is what the three-second wait
 * exists to rule out, and `never-came` is the wait reporting that it ran out.
 * Not a network failure either, since restoring a session from the keychain
 * does not need the network. CAPACITOR-D carried `restore_wait: never-came`
 * together with `stored_token: valid` on 27 members, which is the shape this
 * recognises: a Firebase session that is gone while ours still works.
 *
 * THE ORIGINAL FAILURE IS KEPT as `cause`, because what the plugin said is
 * still the only description of how it failed, and a report that loses it
 * would be worse than this type is good.
 */
export class TheFirebaseSessionNeverCameBack extends Error {
  constructor(public override readonly cause: unknown) {
    super('The Firebase session did not come back after waiting, so the token cannot be renewed');
    this.name = 'TheFirebaseSessionNeverCameBack';
  }
}

/** Whether this failure is the one above, whatever it is wrapped in. */
export const theFirebaseSessionNeverCameBack = (error: unknown): boolean =>
  error instanceof TheFirebaseSessionNeverCameBack;
